/*
 * Vitrine — Design-step coverflow carousel. A "character select" pedestal stage:
 * shoes sit on an arc that curves away into the distance, hero front-and-centre on
 * a lit pedestal, neighbours peeking from the sides. Swipe / arrows / tap rotate
 * the arc; the centred shoe gently turntable-spins.
 *
 * It renders into the SAME WebGLRenderer + canvas the viewer owns (one GL context).
 * The viewer's animate loop drives us: it calls update(dt) then renders getScene()
 * with getCamera() whenever its mode is 'carousel'. We never create a renderer.
 *
 * Public surface:
 *   const c = VitrineCarousel.create({ models, soles, painter, colorway, canvas });
 *   c.setShoes(shoeList)   — (re)build the arc from a list of D.shoes entries.
 *   c.getScene() / c.getCamera()
 *   c.update(dt) / c.resize(w, h)
 *   c.next() / c.prev() / c.goTo(index)
 *   c.getCenterShoe()      — the shoe entry currently in front.
 *   c.onChange(cb)         — cb(shoe, index) whenever the centred shoe changes.
 *   c.setActive(bool)      — gate pointer handlers (off while the viewer is in single mode).
 *   c.dispose()
 */
(function (w) {
  'use strict';

  if (typeof THREE === 'undefined') { console.error('[vitrine] THREE missing (carousel).'); return; }

  // ── Arc + camera tuning. Tweak these to taste; everything else derives from them.
  var STEP_DEG      = 40;     // angular gap between adjacent shoes on the arc (larger = further apart)
  var RADIUS        = 4.2;    // arc radius — larger = gentler recede / further back
  var SHOE_SIZE     = 1.4;    // longest dimension of a normalised shoe, in world units
  var VISIBLE_SPAN  = 3.0;    // shoes within ±this many slots of centre are drawn
  var SPIN_SPEED    = 0.5;    // rad/sec turntable spin of the centred shoe
  var TWEEN_TIME    = 520;    // ms for a next/prev/goTo glide
  var BONE          = '#ECEAE5';
  var SPOT_COLOR    = '#FFF2D2';

  var DEG = Math.PI / 180;

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function create(opts) {
    var P        = opts.painter;
    var models   = opts.models   || {};   // shoeId → preloaded GLTF
    var soles    = opts.soles    || {};   // soleId → preloaded GLTF (ground used under shoes)
    var colorway = opts.colorway || null; // house default colorway for display
    var canvas   = opts.canvas;
    if (!P || !canvas) throw new Error('[vitrine] carousel requires painter + canvas');

    // ── Scene + camera ────────────────────────────────────────────────────────
    var scene = new THREE.Scene();
    scene.background = new THREE.Color(BONE);

    var camera = new THREE.PerspectiveCamera(40, 1, 0.01, 100);
    // Arc radii — circular on wide screens, a long front-to-back oval on portrait
    // phones (set in frameCamera). frameCamera() positions the camera + sets these.
    var arcRx = RADIUS, arcRz = RADIUS;
    frameCamera();

    // Keep the front shoe centred and fully in frame at any aspect. On portrait
    // phones the horizontal FOV is narrow, so we pull the camera back until the
    // hero's width fits; a floor distance keeps the receding shoes in view.
    function frameCamera() {
      var aspect = camera.aspect || 1;
      var portrait = aspect < 1;
      // Portrait: squeeze the arc sideways and stretch it in depth — a long oval —
      // so the neighbours sit further BACK behind the hero and stay inside the
      // narrow frame, instead of a wide circle that throws them off the sides.
      arcRx = portrait ? 1.7 : RADIUS;
      arcRz = portrait ? 5.0 : RADIUS;

      var vFov = camera.fov * DEG;
      var hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      var distForWidth  = (SHOE_SIZE * 0.62) / Math.tan(hFov / 2);
      var distForHeight = 0.62 / Math.tan(vFov / 2);
      var dist = Math.max(distForWidth, distForHeight, 4.8);
      camera.position.set(0, 0.95, dist);
      camera.lookAt(0, 0.45, 0);
    }

    scene.fog = new THREE.Fog(BONE, 7.5, 15);

    // Enhanced lighting with a visible stage spotlight.
    scene.add(new THREE.AmbientLight(0xffffff, 0.48));
    var key = new THREE.DirectionalLight(0xfff8f0, 1.25); key.position.set(2.5, 4, 3);   scene.add(key);
    var fill = new THREE.DirectionalLight(0xf0f4ff, 0.38); fill.position.set(-3, 1.5, 2); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffffff, 0.46); rim.position.set(0, 2, -4);    scene.add(rim);
    
    // A single stage spotlight fixed on the front-centre of the arc. Shoes glide
    // into it, while a cheap translucent sprite suggests the light shaft.
    var spot = new THREE.SpotLight(SPOT_COLOR, 1.65, 9, 18 * DEG, 0.7, 1.0);
    spot.position.set(0, 4.7, 2.9);
    spot.target.position.set(0, 0.0, 0);
    spot.castShadow = false;
    scene.add(spot);
    scene.add(spot.target);
    var spotCone = makeSpotCone(spot.position, spot.target.position, 1.6, 0.075);
    scene.add(spotCone);

    var plank = makeStagePlank(5.0);
    scene.add(plank);

    var POOL_RADIUS = 0.9;

    // Generative backdrop behind the pedestals, tinted to the shown colourway.
    var backdrop = w.VitrineBackdrop ? w.VitrineBackdrop.create({ base: BONE }) : null;
    if (backdrop) {
      scene.add(backdrop.object);
      syncBackdropColors();
    }
    function syncBackdropColors() { setBackdropColorway(colorway); }
    // Tint the generative backdrop to a specific colourway — driven by the theme
    // with the CENTRED shoe's colourway so slide 01's background tracks the shoe in
    // front (each shoe wears its own colourway via the resolver).
    function setBackdropColorway(cw) {
      if (!backdrop || !cw) return;
      backdrop.setColors(
        cw.body1 && cw.body1.color,
        cw.body2 && cw.body2.color,
        cw.sole  && cw.sole.color,
        '#' + scene.background.getHexString()
      );
    }

    // Soft round contact shadow shared by every pedestal (cheap — no shadow maps).
    var shadowTex = makeRadialShadowTexture();

    // ── Slot state ──────────────────────────────────────────────────────────────
    // One "slot" group per shoe, parented to the scene. We lay them out on the arc
    // every frame from `pos` (the floating carousel position; 0 = shoe 0 centred).
    var slots   = [];   // { group, shoe, isGhost }
    var shoes   = [];
    var pos     = 0;    // current floating position
    var tween   = null; // { from, to, t, dur } when gliding
    var spin    = 0;    // accumulated turntable angle for the centred shoe
    var active  = false;
    var lastCenter = -1;
    var changeCb = null;

    // ── Build / rebuild ──────────────────────────────────────────────────────────
    function clearSlots() {
      slots.forEach(function (s) {
        scene.remove(s.group);
        disposeGroup(s.group);
      });
      slots = [];
    }

    function setShoes(list) {
      shoes = (list || []).slice();
      clearSlots();
      shoes.forEach(function (shoe) {
        var slot = shoe.glb && models[shoe.id]
          ? buildShoeSlot(shoe)
          : buildGhostSlot(shoe);
        slots.push({ group: slot, shoe: shoe, isGhost: !(shoe.glb && models[shoe.id]) });
        scene.add(slot);
      });
      // Keep the centred shoe centred if it still exists; otherwise reset to front.
      pos = clamp(Math.round(pos), 0, Math.max(0, slots.length - 1));
      tween = null;
      lastCenter = -1;
      layout();
      fireChange();
    }

    function setModels(newModels) {
      models = newModels || {};
    }

    function setSoles(newSoles) {
      soles = newSoles || {};
    }

    // ── Colorway state ──────────────────────────────────────────────────────────
    var colorwayTimer = null;
    var colorwayList = []; // Will be set by setColorwayList
    var colorwayIndex = 0;
    var colorwayChangeCb = null;
    var isColorwayRotating = false;
    var colorwayResolver = null; // shoeId → colorway, for per-shoe carousel colours

    function setColorwayList(cwList) {
      colorwayList = (cwList || []).slice();
      if (colorwayList.length > 0 && colorwayIndex >= colorwayList.length) {
        colorwayIndex = 0;
      }
    }

    function pickRandomColorway() {
      if (!colorwayList.length) return colorway;
      colorwayIndex = Math.floor(Math.random() * colorwayList.length);
      return colorwayList[colorwayIndex];
    }

    function setColorwayWithName(newColorway, name) {
      setColorway(newColorway);
      if (colorwayChangeCb) colorwayChangeCb(newColorway, name || '');
    }

    function rotateToNextColorway() {
      if (!colorwayList.length) return;
      colorwayIndex = (colorwayIndex + 1) % colorwayList.length;
      var cw = colorwayList[colorwayIndex];
      setColorwayWithName(cw, cw.label || '');
    }

    function startColorwayRotation(intervalMs) {
      isColorwayRotating = true;
      if (colorwayTimer) clearInterval(colorwayTimer);
      rotateToNextColorway();
      colorwayTimer = setInterval(rotateToNextColorway, intervalMs || 10000);
    }

    function stopColorwayRotation() {
      isColorwayRotating = false;
      if (colorwayTimer) {
        clearInterval(colorwayTimer);
        colorwayTimer = null;
      }
    }

    function setColorwayChangeCb(cb) {
      colorwayChangeCb = cb;
    }

    function setThemeFromColorway(cw) {
      if (!cw) return;
      scene.background.set(BONE);
      if (scene.fog) scene.fog.color.set(BONE);
    }

    function setColorway(newColorway) {
      colorway = newColorway || colorway;
      setThemeFromColorway(colorway);
      syncBackdropColors();
      repaintSlots();
    }

    // Per-shoe colourways: the resolver maps a shoe id to the colourway it should
    // wear. When set, each slot paints its own; otherwise they all share `colorway`.
    // opts.tween cross-fades each shoe's colours from what it currently wears, so a
    // palette change reads as the shoes shifting colour — never a slide flash.
    // Staggered so the shoes change colour one after another — fading all at once
    // stutters. Each shoe waits its turn (delay), then glides over TWEEN_DUR_MS.
    var STAGGER_MS = 220;
    var TWEEN_DUR_MS = 420;
    var colorTweenClock = 0;
    var colorTweenActive = false;
    function setColorwayResolver(fn, opts) {
      colorwayResolver = fn || null;
      if (opts && opts.tween) repaintSlotsTweened();
      else repaintSlots();
    }
    function slotColorway(slot) {
      if (colorwayResolver && slot && slot.shoe && slot.shoe.id) {
        var cw = colorwayResolver(slot.shoe.id);
        if (cw) return cw;
      }
      return colorway;
    }
    // Per-shoe paint rules (City, Desert) — see cf-vitrine-catalog-patch.
    function paintShoe(groups, cw, shoeId) {
      if (window.cfPaintShoeColorway) window.cfPaintShoeColorway(P, groups, cw, shoeId);
      else P.applyColorway(groups, cw);
    }
    function shoeColorway(cw, shoeId) {
      return window.cfShoeColorway ? window.cfShoeColorway(cw, shoeId) : cw;
    }
    function paintSlot(slot, cw) {
      if (!slot || slot.isGhost || !slot.group || !cw) return;
      var modelScene = slot.group.userData.modelScene;
      var sideMeshes = slot.group.userData.soleSideMeshes || [];
      var shoeId = slot.shoe && slot.shoe.id;
      cw = shoeColorway(cw, shoeId);
      if (modelScene) paintShoe(slot.group.userData.paintGroups || P.groupByTexture(modelScene), cw, shoeId);
      if (cw.sole && sideMeshes.length) P.paintMeshes(sideMeshes, cw.sole.color);
    }
    // An in-between colourway, each slot colour lerped from → to.
    function lerpColorway(from, to, t) {
      function L(key) {
        var a = from[key] && from[key].color, b = to[key] && to[key].color;
        return { color: mixHex(a, b, t) || b || a, label: (to[key] && to[key].label) || '' };
      }
      return { body1: L('body1'), body2: L('body2'), sole: L('sole'), laces: L('laces'), stitch: L('stitch') };
    }
    function repaintSlots() {
      slots.forEach(function (slot) {
        if (!slot || slot.isGhost || !slot.group) return;
        var cw = slotColorway(slot);
        if (!cw) return;
        paintSlot(slot, cw);
        slot.group.userData.currentColorway = cw;
      });
    }
    // Capture each changing slot's from/to and a staggered start delay, then let
    // stepColorTween glide them one after another.
    function repaintSlotsTweened() {
      var order = 0, any = false;
      slots.forEach(function (slot) {
        if (!slot || slot.isGhost || !slot.group) return;
        var to = slotColorway(slot);
        if (!to) return;
        var from = slot.group.userData.currentColorway || to;
        if (from && to && from.id === to.id) {   // no change for this shoe
          slot.group.userData.cwTween = null;
          slot.group.userData.currentColorway = to;
          return;
        }
        slot.group.userData.cwTween = { from: from, to: to, delay: order * STAGGER_MS, done: false };
        order++;
        any = true;
      });
      if (!any) return;
      colorTweenClock = 0;
      colorTweenActive = true;
    }
    // Advance the staggered colour fades; called each frame from update(). Each
    // shoe only repaints while it's inside its own [delay, delay+dur) window, so
    // at most a couple repaint per frame instead of all of them.
    function stepColorTween(dt) {
      if (!colorTweenActive) return;
      colorTweenClock += dt * 1000;
      var stillActive = false;
      slots.forEach(function (slot) {
        if (!slot || slot.isGhost || !slot.group) return;
        var tw = slot.group.userData.cwTween;
        if (!tw || tw.done) return;
        var local = colorTweenClock - tw.delay;
        if (local < 0) { stillActive = true; return; }       // waiting its turn
        if (local >= TWEEN_DUR_MS) {                          // arrived
          paintSlot(slot, tw.to);
          slot.group.userData.currentColorway = tw.to;
          tw.done = true;
          return;
        }
        stillActive = true;
        if (slot.group.visible !== false) {
          paintSlot(slot, lerpColorway(tw.from, tw.to, easeOutCubic(local / TWEEN_DUR_MS)));
        }
      });
      if (!stillActive) colorTweenActive = false;
    }

    // A real shoe: clone the preloaded model (+ ground sole) so it looks complete,
    // paint the house colorway, normalise it onto the pedestal. Mirrors the
    // offscreen-thumbnail pipeline in the viewer (clones share geometry with the
    // originals — never dispose cloned shoe meshes, only resources we create).
    function buildShoeSlot(shoe) {
      var group = new THREE.Group();
      var inner = new THREE.Group(); // spinnable contents: shoe + sole

      var modelScene = models[shoe.id].scene.clone(true);
      P.fixSceneDepth(modelScene);
      modelScene.traverse(function (o) { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
      inner.add(modelScene);
      group.userData.modelScene = modelScene;
      group.userData.paintGroups = P.groupByTexture(modelScene);

      var sideMeshes = [];
      if (soles.ground) {
        var soleScene = soles.ground.scene.clone(true);
        P.fixSceneDepth(soleScene);
        soleScene.traverse(function (o) { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
        inner.add(soleScene);
        if (P.fitSoleToUpper) {
          var fitId = modelScene.userData && modelScene.userData.cfShoeId;
          P.fitSoleToUpper(modelScene, soleScene, (fitId && window.cfSoleFitOptions) ? window.cfSoleFitOptions(fitId) : undefined);
        }
        soleScene.traverse(function (o) { if (o.isMesh && o.name === 'Side_1') sideMeshes.push(o); });
      }
      group.userData.soleSideMeshes = sideMeshes;

      var slotCw = (colorwayResolver && shoe && shoe.id && colorwayResolver(shoe.id)) || colorway;
      if (slotCw) {
        var wornCw = shoeColorway(slotCw, shoe && shoe.id);
        paintShoe(group.userData.paintGroups, wornCw, shoe && shoe.id);
        if (wornCw.sole && sideMeshes.length) P.paintMeshes(sideMeshes, wornCw.sole.color);
        group.userData.currentColorway = slotCw;
      }

      // Normalise the combined shoe+sole: centre on X/Z, rest the base on y = 0,
      // scale to SHOE_SIZE. The scaled holder is the spinnable element.
      var box    = new THREE.Box3().setFromObject(inner);
      var size   = box.getSize(new THREE.Vector3());
      var center = box.getCenter(new THREE.Vector3());
      var maxDim = Math.max(size.x, size.y, size.z) || 1;
      var holder = new THREE.Group();
      inner.position.set(-center.x, -box.min.y, -center.z);
      holder.add(inner);
      holder.scale.setScalar(SHOE_SIZE / maxDim);
      group.add(holder);
      group.userData.shoe = holder;

      group.add(makePedestal());
      var pool = makeSpotPool();
      group.add(pool);
      group.userData.pool = pool;
      group.add(makeShadow());
      return group;
    }

    // Coming-soon shoe: a pedestal with a floating "Soon" plaque, no model.
    function buildGhostSlot(shoe) {
      var group = new THREE.Group();
      group.add(makePedestal());
      var pool = makeSpotPool();
      group.add(pool);
      group.userData.pool = pool;
      group.add(makeShadow());
      var plaque = makeTextPlane('SOON');
      plaque.position.set(0, 0.7, 0);
      group.add(plaque);
      return group;
    }

    function makePedestal() {
      var geo = new THREE.CylinderGeometry(0.62, 0.7, 0.12, 48);
      // A contrasting neutral taupe — darker than the bone backdrop so the shoe
      // reads against it, matte so the spotlight doesn't blow it out to white.
      var mat = new THREE.MeshStandardMaterial({ color: '#938C81', roughness: 0.95, metalness: 0.0 });
      var m   = new THREE.Mesh(geo, mat);
      m.position.y = -0.06; // top face at y = 0 so the shoe sits flush
      m.receiveShadow = true;
      m.userData.cfOwned = true;
      return m;
    }

    // Soft round glow pool on the floor under a shoe. Built per shoe (so it sits
    // exactly under that pedestal), but layout() fades it to near-nothing for every
    // shoe except the centred one — so only the shoe in the light reads as spotlit.
    function makeSpotPool() {
      var geo = new THREE.CircleGeometry(POOL_RADIUS, 48);
      var mat = new THREE.MeshBasicMaterial({
        color: SPOT_COLOR,
        transparent: true,
        opacity: 0.08,
        depthWrite: false
      });
      var m = new THREE.Mesh(geo, mat);
      m.rotation.x = -Math.PI / 2;
      // Sit the glow at the BASE of the pedestal (its bottom is at y = -0.12),
      // just above the contact shadow, so the oval reads as light on the floor
      // rather than a halo around the shoe's feet.
      m.position.y = -0.115;
      m.renderOrder = 1;
      m.userData.cfOwned = true;
      return m;
    }

    function makeShadow() {
      // shadowTex is shared across all slots — disposed once in dispose(), never per-slot.
      var mat = new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false, opacity: 0.4 });
      var m   = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 1.8), mat);
      m.rotation.x = -Math.PI / 2;
      m.position.y = -0.119; // just under the pedestal
      m.userData.cfOwned = true;
      return m;
    }

    // ── Per-frame arc layout ──────────────────────────────────────────────────────
    function angleFor(i)  { return (i - pos) * STEP_DEG * DEG; }
    function centerIndex() { return clamp(Math.round(pos), 0, Math.max(0, slots.length - 1)); }

    function layout() {
      var ci = centerIndex();
      for (var i = 0; i < slots.length; i++) {
        var slot = slots[i];
        var g = slot.group;
        var a = angleFor(i);
        var dist = Math.abs(i - pos);
        if (dist > VISIBLE_SPAN) { g.visible = false; continue; }
        g.visible = true;

        // Arc: ellipse centred behind the hero so the front point sits at origin.
        // arcRx/arcRz are equal (circle) on desktop, a long oval on portrait.
        g.position.x = arcRx * Math.sin(a);
        g.position.z = arcRz * (Math.cos(a) - 1);

        // Recede: smaller + slightly lower as it curves away.
        var s = clamp(0.6 + 0.4 * Math.cos(a), 0.5, 1);
        g.scale.setScalar(s);
        g.position.y = (1 - s) * -0.15;

        // Only the centred shoe is "in the light": its floor pool is full, the
        // neighbours' pools fade out fast with distance so they don't read as lit.
        if (slot.group.userData.pool) {
          slot.group.userData.pool.material.opacity = 0.08 * clamp(1 - dist * 1.3, 0, 1);
        }

        // Yaw side shoes partly toward the front for the coverflow read.
        var yaw = -a * 0.55;
        // The centred real shoe slowly spins on its pedestal.
        if (i === ci && !slot.isGhost && slot.group.userData.shoe) {
          slot.group.userData.shoe.rotation.y = spin;
          g.rotation.y = 0;
        } else {
          if (slot.group.userData.shoe) slot.group.userData.shoe.rotation.y = 0;
          g.rotation.y = yaw;
        }
      }
    }

    // ── Animation ────────────────────────────────────────────────────────────────
    function update(dt) {
      spin += dt * SPIN_SPEED;
      if (backdrop) backdrop.update(dt);
      if (tween) {
        tween.t += dt * 1000;
        var k = clamp(tween.t / tween.dur, 0, 1);
        pos = tween.from + (tween.to - tween.from) * easeOutCubic(k);
        if (k >= 1) { pos = tween.to; tween = null; }
      }
      stepColorTween(dt);
      layout();
      fireChange();
    }

    function fireChange() {
      var ci = centerIndex();
      if (ci === lastCenter || !slots.length) return;
      lastCenter = ci;
      if (changeCb) changeCb(slots[ci].shoe, ci);
    }

    // ── Navigation ───────────────────────────────────────────────────────────────
    function glideTo(target) {
      target = clamp(target, 0, Math.max(0, slots.length - 1));
      tween = { from: pos, to: target, t: 0, dur: TWEEN_TIME };
    }
    function next() { glideTo(Math.round(pos) + 1); }
    function prev() { glideTo(Math.round(pos) - 1); }
    function goTo(i) { glideTo(i); }

    function getCenterShoe() { return slots.length ? slots[centerIndex()].shoe : null; }

    // ── Pointer: drag-to-swipe, tap-a-side-shoe-to-advance ────────────────────────
    var dragging = false, startX = 0, startPos = 0, moved = 0;
    function widthDragToSlots(px) {
      var wpx = canvas.clientWidth || 1;
      return (px / wpx) * 3.2; // a full-width drag ≈ 3 shoes
    }
    function onDown(e) {
      if (!active) return;
      dragging = true; moved = 0;
      startX = e.clientX; startPos = pos; tween = null;
      canvas.setPointerCapture && canvas.setPointerCapture(e.pointerId);
    }
    function onMove(e) {
      if (!active || !dragging) return;
      var dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      pos = clamp(startPos - widthDragToSlots(dx), -0.5, slots.length - 0.5);
    }
    function onUp(e) {
      if (!active || !dragging) return;
      dragging = false;
      if (moved < 6) {
        // A tap: step toward the side that was tapped (peeking shoe → front).
        var rect = canvas.getBoundingClientRect();
        var rel  = (e.clientX - rect.left) / rect.width;
        if (rel > 0.62) next();
        else if (rel < 0.38) prev();
        else glideTo(Math.round(pos));
      } else {
        glideTo(Math.round(pos));
      }
    }

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);

    // Mouse-wheel / trackpad steps through the shoes (throttled to one step per
    // notch). Only while the carousel owns the canvas — in single-model mode this
    // bails before preventDefault so OrbitControls' wheel-zoom still works.
    var lastWheel = 0;
    function onWheel(e) {
      if (!active) return;
      e.preventDefault();
      var now = performance.now();
      if (now - lastWheel < 200) return;
      var d = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(d) < 2) return;
      lastWheel = now;
      d > 0 ? next() : prev();
    }
    canvas.addEventListener('wheel', onWheel, { passive: false });

    function resize(wd, ht) {
      if (!wd || !ht) return;
      camera.aspect = wd / ht;
      frameCamera();
      camera.updateProjectionMatrix();
    }

    function dispose() {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('wheel', onWheel);
      stopColorwayRotation();
      clearSlots();
      if (backdrop) { scene.remove(backdrop.object); backdrop.dispose(); }
      scene.remove(plank);
      disposeMesh(plank);
      scene.remove(spotCone);
      disposeMesh(spotCone);
      if (shadowTex && shadowTex.dispose) shadowTex.dispose();
    }

    return {
      getScene:      function () { return scene; },
      getCamera:     function () { return camera; },
      setShoes:      setShoes,
      setModels:     setModels,
      setSoles:      setSoles,
      setColorway:   setColorway,
      setBackdropColorway: setBackdropColorway,
      setColorwayResolver: setColorwayResolver,
      setColorwayList: setColorwayList,
      pickRandomColorway: pickRandomColorway,
      startColorwayRotation: startColorwayRotation,
      stopColorwayRotation: stopColorwayRotation,
      setColorwayChangeCb: setColorwayChangeCb,
      update:        update,
      resize:        resize,
      next:          next,
      prev:          prev,
      goTo:          goTo,
      getCenterShoe: getCenterShoe,
      getCenterIndex: centerIndex,
      onChange:      function (cb) { changeCb = cb; },
      setActive:     function (b) { active = !!b; if (!b) dragging = false; },
      dispose:       dispose
    };
  }

  // ── Texture helpers ──────────────────────────────────────────────────────────
  function makeRadialShadowTexture() {
    var c = document.createElement('canvas');
    c.width = c.height = 128;
    var ctx = c.getContext('2d');
    var g = ctx.createRadialGradient(64, 64, 4, 64, 64, 60);
    g.addColorStop(0, 'rgba(0,0,0,0.55)');
    g.addColorStop(0.6, 'rgba(0,0,0,0.18)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 128, 128);
    var tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    return tex;
  }

  // A round wooden plank the whole stage sits on — shoes and pedestals rest on it,
  // and the bone backdrop now shows around its curved edge instead of an
  // edge-to-edge floor. Sized generously and pushed back just enough that the
  // curved front edge still reaches forward of the hero, so the front-centre
  // spotlight's scatter lands on the wood rather than spilling off a hard edge
  // into the void. A slight thickness gives it a visible rim so it reads as a
  // raised platform; the top face sits at y = -0.13, just under the pedestals.
  function makeStagePlank(radius) {
    var thickness = 0.16;
    var geo = new THREE.CylinderGeometry(radius, radius, thickness, 96);
    var tex = makePlankTexture();
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(radius * 1.5, radius * 1.5); // plank lines at roughly the old board scale
    var mat = new THREE.MeshStandardMaterial({
      color: '#AFA79B',
      map: tex,
      roughness: 0.96,
      metalness: 0.0
    });
    var m = new THREE.Mesh(geo, mat);
    m.position.set(0, -0.13 - thickness / 2, -2.2);
    m.receiveShadow = true;
    m.userData.cfOwned = true;
    m.userData.cfOwnTexture = true;
    return m;
  }

  function makeSpotCone(lightPos, targetPos, radius, opacity) {
    var dir = lightPos.clone().sub(targetPos);
    var height = dir.length();
    var tex = makeBeamTexture();
    var mat = new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      opacity: opacity || 0.14,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    var cone = new THREE.Sprite(mat);
    cone.position.copy(lightPos).add(targetPos).multiplyScalar(0.5);
    cone.scale.set((radius || 1) * 1.4, height, 1);
    cone.renderOrder = 2;
    cone.frustumCulled = false;
    cone.userData.cfOwned = true;
    cone.userData.cfOwnTexture = true;
    return cone;
  }

  function makeBeamTexture() {
    var c = document.createElement('canvas');
    c.width = 128; c.height = 512;
    var ctx = c.getContext('2d');
    var radial = ctx.createRadialGradient(64, 250, 4, 64, 250, 62);
    radial.addColorStop(0, 'rgba(255,242,210,0.55)');
    radial.addColorStop(0.42, 'rgba(255,242,210,0.18)');
    radial.addColorStop(1, 'rgba(255,242,210,0)');
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, 128, 512);
    var fade = ctx.createLinearGradient(0, 0, 0, 512);
    fade.addColorStop(0, 'rgba(255,242,210,0)');
    fade.addColorStop(0.22, 'rgba(255,242,210,0.45)');
    fade.addColorStop(0.78, 'rgba(255,242,210,0.2)');
    fade.addColorStop(1, 'rgba(255,242,210,0)');
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = fade;
    ctx.fillRect(0, 0, 128, 512);
    var tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    return tex;
  }

  function makePlankTexture() {
    var c = document.createElement('canvas');
    c.width = c.height = 256;
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#B4AB9F';
    ctx.fillRect(0, 0, 256, 256);
    for (var y = 0; y < 256; y += 16) {
      ctx.fillStyle = y % 32 === 0 ? 'rgba(255,255,255,0.065)' : 'rgba(0,0,0,0.055)';
      ctx.fillRect(0, y, 256, 1);
    }
    for (var x = 0; x < 256; x += 64) {
      ctx.fillStyle = 'rgba(40,34,28,0.08)';
      ctx.fillRect(x, 0, 1, 256);
      ctx.fillStyle = 'rgba(255,255,255,0.035)';
      ctx.fillRect(x + 1, 0, 1, 256);
    }
    for (var i = 0; i < 1800; i++) {
      var v = 95 + Math.floor(Math.random() * 75);
      ctx.fillStyle = 'rgba(' + v + ',' + v + ',' + v + ',0.06)';
      ctx.fillRect(Math.random() * 256, Math.random() * 256, Math.random() > 0.9 ? 2 : 1, 1);
    }
    var tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    return tex;
  }

  function disposeMesh(m) {
    if (!m) return;
    if (m.geometry) m.geometry.dispose();
    var mats = Array.isArray(m.material) ? m.material : [m.material];
    mats.forEach(function (mat) {
      if (!mat) return;
      if (m.userData.cfOwnTexture && mat.map) mat.map.dispose();
      if (mat.dispose) mat.dispose();
    });
  }

  function mixHex(a, b, t) {
    if (!a && !b) return null;
    if (!a) return b;
    if (!b) return a;
    var ca = new THREE.Color(a);
    var cb = new THREE.Color(b);
    ca.lerp(cb, typeof t === 'number' ? t : 0.5);
    return '#' + ca.getHexString();
  }

  function makeTextPlane(text) {
    var c = document.createElement('canvas');
    c.width = 256; c.height = 128;
    var ctx = c.getContext('2d');
    ctx.clearRect(0, 0, 256, 128);
    ctx.fillStyle = '#5A5750';
    ctx.font = '600 54px "Space Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 128, 64);
    var tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    var mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide });
    var m = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.45), mat);
    m.userData.cfOwned = true;
    m.userData.cfOwnTexture = true; // the plaque's CanvasTexture is ours to free
    return m;
  }

  // Only free resources the carousel created (pedestals, shadow planes, plaques).
  // Cloned shoe meshes share geometry — and unpainted meshes share materials +
  // textures — with the preloaded originals, so disposing them would corrupt the
  // live viewer and offscreen thumbnails. We leave those clones for the GC.
  function disposeGroup(group) {
    group.traverse(function (o) {
      if (!o.isMesh || !o.userData.cfOwned) return;
      if (o.geometry) o.geometry.dispose();
      var mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach(function (m) {
        if (!m) return;
        if (o.userData.cfOwnTexture && m.map) m.map.dispose();
        if (m.dispose) m.dispose();
      });
    });
  }

  w.VitrineCarousel = { create: create };
})(window);
